import React, { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { kr } from '../lib/format';
import { PageHeader, Card, Modal } from '../components/ui';
import { Target, Plus, Trash2, BarChart3, Save } from 'lucide-react';

const MND = ['Jan', 'Feb', 'Mar', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Des'];

const Budsjett = () => {
  const aar = new Date().getFullYear();
  const [valgtAar, setValgtAar] = useState(aar);
  const [rader, setRader] = useState([]);
  const [sammenlign, setSammenlign] = useState([]);
  const [visning, setVisning] = useState('rediger'); // rediger | sammenligning
  const [skjema, setSkjema] = useState(null);
  const [konti, setKonti] = useState([]);

  const last = useCallback(() => {
    api.get('/budsjett', { params: { aar: valgtAar } }).then(r => setRader(r.data));
    if (visning === 'sammenligning') {
      api.get('/budsjett/sammenligning', { params: { aar: valgtAar } }).then(r => setSammenlign(r.data.rader));
    }
  }, [valgtAar, visning]);
  useEffect(() => { last(); }, [last]);
  useEffect(() => {
    api.get('/kontoplan').then(r => setKonti(r.data)).catch(() => {});
  }, []);

  const apneNy = () => setSkjema({
    konto: '', aar: valgtAar, manedsbelop: Array(12).fill(0), notat: '',
  });
  const apneRediger = (b) => setSkjema({
    id: b.id, konto: b.konto, aar: b.aar, manedsbelop: [...b.manedsbelop], notat: b.notat,
  });

  const fordelLikt = () => {
    const sum = parseFloat(skjema.totalSum) || 0;
    const pr = sum / 12;
    setSkjema({ ...skjema, manedsbelop: Array(12).fill(Math.round(pr * 100) / 100) });
  };

  const lagre = async () => {
    if (!skjema.konto) { alert('Velg konto'); return; }
    await api.post('/budsjett', {
      aar: skjema.aar, konto: skjema.konto, notat: skjema.notat,
      manedsbelop: skjema.manedsbelop.map(v => parseFloat(v) || 0),
    });
    setSkjema(null); last();
  };

  const slett = async (b) => {
    if (!window.confirm(`Slett budsjett for konto ${b.konto}?`)) return;
    await api.delete(`/budsjett/${b.id}`); last();
  };

  return (
    <div>
      <PageHeader title="Budsjett" subtitle={`Budsjett for ${valgtAar}`} actions={
        <>
          <select className="fm-input" value={valgtAar} onChange={(e) => setValgtAar(parseInt(e.target.value))} style={{ width: 100 }}>
            {[aar - 1, aar, aar + 1].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button className={visning === 'rediger' ? 'btn-primary' : 'btn-ghost'} onClick={() => setVisning('rediger')}>
            <Target size={14} /> Budsjett
          </button>
          <button className={visning === 'sammenligning' ? 'btn-primary' : 'btn-ghost'} onClick={() => setVisning('sammenligning')}>
            <BarChart3 size={14} /> Sammenlign mot faktisk
          </button>
          {visning === 'rediger' && <button className="btn-primary" onClick={apneNy}><Plus size={14} /> Ny linje</button>}
        </>
      } />

      {visning === 'rediger' && (
        <Card style={{ overflowX: 'auto' }}>
          <table className="fm-table" style={{ minWidth: 1100 }}>
            <thead><tr>
              <th>Konto</th>
              {MND.map(m => <th key={m} style={{ textAlign: 'right' }}>{m}</th>)}
              <th style={{ textAlign: 'right' }}>Sum</th>
              <th></th>
            </tr></thead>
            <tbody>
              {rader.map(b => (
                <tr key={b.id} onClick={() => apneRediger(b)} style={{ cursor: 'pointer' }}>
                  <td>{b.konto}</td>
                  {b.manedsbelop.map((v, i) => <td key={i} style={{ textAlign: 'right' }}>{kr(v)}</td>)}
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{kr(b.sum_aar)}</td>
                  <td onClick={(e) => { e.stopPropagation(); slett(b); }}>
                    <button className="btn-ghost" style={{ color: 'var(--theme-danger)' }}><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
              {!rader.length && <tr><td colSpan={15} className="fm-text-muted" style={{ textAlign: 'center', padding: 24 }}>Ingen budsjett-linjer for {valgtAar}.</td></tr>}
            </tbody>
          </table>
        </Card>
      )}

      {visning === 'sammenligning' && (
        <Card style={{ overflowX: 'auto' }}>
          <table className="fm-table" style={{ minWidth: 1200 }}>
            <thead>
              <tr>
                <th rowSpan={2}>Konto</th>
                <th rowSpan={2}>Navn</th>
                <th colSpan={3} style={{ textAlign: 'center', borderLeft: '1px solid var(--theme-border)' }}>Sum år</th>
                <th colSpan={12} style={{ textAlign: 'center', borderLeft: '1px solid var(--theme-border)' }}>Avvik per måned (faktisk - budsjett)</th>
              </tr>
              <tr>
                <th style={{ textAlign: 'right', borderLeft: '1px solid var(--theme-border)' }}>Budsjett</th>
                <th style={{ textAlign: 'right' }}>Faktisk</th>
                <th style={{ textAlign: 'right' }}>Avvik</th>
                {MND.map((m, i) => <th key={i} style={{ textAlign: 'right', borderLeft: i === 0 ? '1px solid var(--theme-border)' : 'none' }}>{m}</th>)}
              </tr>
            </thead>
            <tbody>
              {sammenlign.map(r => (
                <tr key={r.konto}>
                  <td>{r.konto}</td>
                  <td>{r.kontonavn}</td>
                  <td style={{ textAlign: 'right', borderLeft: '1px solid var(--theme-border)' }}>{kr(r.sum_budsjett)}</td>
                  <td style={{ textAlign: 'right' }}>{kr(r.sum_faktisk)}</td>
                  <td style={{ textAlign: 'right', color: r.sum_avvik >= 0 ? 'var(--theme-success)' : 'var(--theme-danger)' }}>{kr(r.sum_avvik)}</td>
                  {r.avvik.map((a, i) => (
                    <td key={i} style={{ textAlign: 'right', color: a >= 0 ? 'var(--theme-success)' : 'var(--theme-danger)', borderLeft: i === 0 ? '1px solid var(--theme-border)' : 'none' }}>
                      {a !== 0 ? kr(a) : '—'}
                    </td>
                  ))}
                </tr>
              ))}
              {!sammenlign.length && <tr><td colSpan={17} className="fm-text-muted" style={{ textAlign: 'center', padding: 24 }}>Ingen data.</td></tr>}
            </tbody>
          </table>
        </Card>
      )}

      {skjema && (
        <Modal open onClose={() => setSkjema(null)} width={760}
               title={skjema.id ? `Rediger budsjett – konto ${skjema.konto}` : 'Ny budsjett-linje'}
               footer={<><button className="btn-ghost" onClick={() => setSkjema(null)}>Avbryt</button>
                         <button className="btn-primary" onClick={lagre}><Save size={14} /> Lagre</button></>}>
          <div style={{ display: 'grid', gap: 10 }}>
            <div><label>Konto</label>
              <select className="fm-input" value={skjema.konto} onChange={(e) => setSkjema({ ...skjema, konto: e.target.value })}>
                <option value="">– velg –</option>
                {konti.filter(k => /^[34567]/.test(k.nummer)).map(k => (
                  <option key={k.nummer} value={k.nummer}>{k.nummer} – {k.navn}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'end' }}>
              <div style={{ flex: 1 }}>
                <label>Total år (fordel likt på 12 mnd)</label>
                <input type="number" className="fm-input" value={skjema.totalSum || ''}
                       onChange={(e) => setSkjema({ ...skjema, totalSum: e.target.value })} />
              </div>
              <button className="btn-secondary" onClick={fordelLikt}>Fordel likt</button>
            </div>
            <div>
              <label>Per måned</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 6 }}>
                {MND.map((m, i) => (
                  <div key={i}>
                    <label className="fm-text-muted" style={{ fontSize: 11 }}>{m}</label>
                    <input type="number" className="fm-input" value={skjema.manedsbelop[i]}
                           onChange={(e) => {
                             const nye = [...skjema.manedsbelop];
                             nye[i] = e.target.value;
                             setSkjema({ ...skjema, manedsbelop: nye });
                           }} />
                  </div>
                ))}
              </div>
            </div>
            <div><label>Notat</label>
              <input className="fm-input" value={skjema.notat || ''} onChange={(e) => setSkjema({ ...skjema, notat: e.target.value })} /></div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Budsjett;
